import { types, Instance, flow, getRoot } from 'mobx-state-tree';
import { io, Socket } from 'socket.io-client';

/**
 * WorkspaceRealtimeStore - Manages WebSocket connection for workspace-level real-time updates
 * 
 * Architecture:
 * - Single WebSocket connection per workspace
 * - User subscribes to workspace:{orgId} channel
 * - All updates (mindmaps, chats, presence) broadcast through this channel
 * - Patch handlers route updates to appropriate stores
 */
export const WorkspaceRealtimeStore = types
  .model('WorkspaceRealtimeStore', {
    isConnected: types.optional(types.boolean, false),
    currentWorkspaceId: types.maybeNull(types.string),
    connectionError: types.maybeNull(types.string),
    currentMindmapId: types.maybeNull(types.string), // Track which mindmap user is viewing
  })
  .volatile(self => ({
    socket: null as Socket | null,
    presencePingInterval: null as NodeJS.Timeout | null,
    cursorThrottleTime: 0, // Track last cursor send time for throttling
  }))
  .views(self => ({
    get isAuthenticated() {
      const rootStore = getRoot(self) as any;
      return rootStore.auth?.isReallyAuthenticated || false;
    },
    
    get organizationId() {
      const rootStore = getRoot(self) as any;
      return rootStore.organization?.organizationId;
    },
  }))
  .actions(self => ({
    setConnected(connected: boolean) {
      self.isConnected = connected;
      if (connected) {
        self.connectionError = null;
      }
    },
    
    setConnectionError(error: string) {
      self.connectionError = error;
      self.isConnected = false;
    },
    
    // Connect to workspace WebSocket
    connectToWorkspace: flow(function* (orgId: string) {
      if (self.socket?.connected && self.currentWorkspaceId === orgId) {
        console.log(`🔗 [WorkspaceRealtime] Already connected to workspace: ${orgId}`);
        return;
      }
      
      // Disconnect from previous workspace if any
      if (self.socket) {
        (self as any).disconnectFromWorkspace();
      }
      
      try {
        console.log(`🚀 [WorkspaceRealtime] Connecting to workspace: ${orgId}...`);
        
        const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:4011';
        
        // Create WebSocket connection to /workspace namespace
        const rootStore = getRoot(self) as any;
        const token = rootStore.auth?.token;
        
        console.log(`🔑 [WorkspaceRealtime] Creating socket with token: ${token ? 'YES' : 'NO'}`);
        
        self.socket = io(`${backendUrl}/workspace`, {
          auth: {
            token, // Send JWT for WebSocket auth
          },
          withCredentials: true,
          transports: ['websocket', 'polling'],
        });
        
        self.socket.on('connect', () => {
          console.log(`✅ [WorkspaceRealtime] Connected to workspace socket`);
          (self as any).setConnected(true);
          
          const rootStore = getRoot(self) as any;
          const userId = rootStore.auth?.user?.id;
          
          // Join workspace channel with userId
          self.socket?.emit('join:workspace', { workspaceId: orgId, userId });
          
          // Start presence ping
          (self as any).startPresencePing();
        });
        
        self.socket.on('disconnect', () => {
          console.log(`❌ [WorkspaceRealtime] Disconnected from workspace`);
          (self as any).setConnected(false);
          (self as any).stopPresencePing();
        });
        
        self.socket.on('connect_error', (error: Error) => {
          console.error(`❌ [WorkspaceRealtime] Connection error:`, error.message);
          (self as any).setConnectionError(error.message);
          (self as any).setConnected(false);
        });
        
        // Listen for workspace updates
        self.socket.on('workspace:update', (event: any) => {
          (self as any).handleWorkspaceUpdate(event);
        });
        
        // Listen for workspace joined with session list
        self.socket.on('workspace:joined', (data: any) => {
          if (data.sessions) {
            (self as any).handleSessionList(data.sessions);
          }
        });
        
        // Listen for session list updates
        self.socket.on('presence:sessions', (data: any) => {
          (self as any).handleSessionList(data.sessions);
        });
        
        // Listen for presence pong (silent)
        self.socket.on('presence:pong', () => {
          // Acknowledged
        });
        
        // Listen for cursor updates from other users
        self.socket.on('cursor:update', (cursorData: any) => {
          (self as any).handleCursorUpdate(cursorData);
        });
        
        self.currentWorkspaceId = orgId;
        console.log(`🔗 [WorkspaceRealtime] Subscribed to workspace:${orgId}`);
        
        // Explicitly connect (socket created in disconnected state)
        if (self.socket && !self.socket.connected) {
          self.socket.connect();
        }
        
      } catch (error) {
        console.error(`❌ [WorkspaceRealtime] Failed to connect:`, error);
        self.connectionError = error.message;
        throw error;
      }
    }),
    
    disconnectFromWorkspace() {
      if (self.socket) {
        console.log(`🔌 [WorkspaceRealtime] Disconnecting from workspace: ${self.currentWorkspaceId}`);
        (self as any).stopPresencePing();
        self.socket.disconnect();
        self.socket = null;
        self.isConnected = false;
        self.currentWorkspaceId = null;
      }
    },
    
    /**
     * Start presence ping heartbeat
     */
    startPresencePing() {
      (self as any).stopPresencePing();
      
      const rootStore = getRoot(self) as any;
      const userId = rootStore.auth?.user?.id;
      if (!userId) return;
      
      // Send initial ping
      self.socket?.emit('presence:ping', { userId });
      
      // Ping every 15 seconds
      self.presencePingInterval = setInterval(() => {
        if (self.socket?.connected) {
          self.socket.emit('presence:ping', { userId });
        }
      }, 15000);
    },
    
    /**
     * Stop presence ping
     */
    stopPresencePing() {
      if (self.presencePingInterval) {
        clearInterval(self.presencePingInterval);
        self.presencePingInterval = null;
      }
    },
    
    /**
     * Handle session list from backend
     */
    handleSessionList(sessions: any[] | any) {
      const rootStore = getRoot(self) as any;
      if (rootStore.onlinePresence) {
        // Handle both array and object with sessions property
        const sessionArray = Array.isArray(sessions) ? sessions : sessions?.sessions;
        rootStore.onlinePresence.updateSessions(sessionArray);
      }
    },
    
    // Route workspace updates to appropriate stores
    handleWorkspaceUpdate(event: any) {
      // Log major events only, not every update
      if (event.type.includes('created') || event.type.includes('deleted')) {
        console.log(`📥 [WorkspaceRealtime] Received update:`, event.type, event.entityId);
      }
      
      const rootStore = getRoot(self) as any;
      const { type, entityId, data, userId } = event;
      
      // Skip updates from ourselves if they're optimistic
      const currentUserId = rootStore.auth?.user?.id;
      if (userId === currentUserId && data?.isOptimistic) {
        // Silently skip own optimistic updates
        return;
      }
      
      try {
        // Route to appropriate store based on event type
        if (type.startsWith('mindmap:')) {
          (self as any).handleMindmapUpdate(event);
        } else if (type.startsWith('chat:')) {
          (self as any).handleChatUpdate(event);
        } else if (type.startsWith('presence:')) {
          (self as any).handlePresenceUpdate(event);
        } else {
          console.warn(`⚠️  [WorkspaceRealtime] Unknown event type: ${type}`);
        }
      } catch (error) {
        console.error(`❌ [WorkspaceRealtime] Failed to handle update:`, error);
      }
    },
    
    // Handle mindmap updates
    handleMindmapUpdate(event: any) {
      const rootStore = getRoot(self) as any;
      const { type, data } = event;
      
      switch (type) {
        case 'mindmap:created':
          // Mindmap created (logged above in general handler)
          rootStore.organization?.addMindmapFromWebSocket(data);
          break;
          
        case 'mindmap:updated':
          // Mindmap updated (silently handled)
          rootStore.organization?.updateMindmapFromWebSocket(data);
          break;
          
        case 'mindmap:deleted':
          // Mindmap deleted (logged above in general handler)
          rootStore.organization?.removeMindmapFromWebSocket(data.id);
          break;
          
        case 'mindmap:node:created':
        case 'mindmap:node:updated':
        case 'mindmap:node:deleted':
          // Node update (silently handled)
          // Pass the full event so handler knows the type
          rootStore.mindmap?.handleWebSocketPatch({ ...event, ...data });
          break;
          
        default:
          console.warn(`⚠️  [WorkspaceRealtime] Unhandled mindmap event: ${type}`);
      }
    },
    
    // Handle chat updates  
    handleChatUpdate(event: any) {
      const rootStore = getRoot(self) as any;
      const { type, data } = event;
      
      switch (type) {
        case 'chat:conversation:created':
          // Conversation created (logged above in general handler)
          rootStore.chat?.addConversationFromWebSocket(data);
          break;
          
        case 'chat:conversation:updated':
          // Conversation updated (silently handled)
          rootStore.chat?.updateConversationFromWebSocket(data);
          break;
          
        case 'chat:conversation:deleted':
          // Conversation deleted (logged above in general handler)
          rootStore.chat?.removeConversationFromWebSocket(data.id);
          break;
          
        case 'chat:message:created':
          // Message created (silently handled)
          rootStore.chat?.addMessageFromWebSocket(data);
          break;
          
        default:
          console.warn(`⚠️  [WorkspaceRealtime] Unhandled chat event: ${type}`);
      }
    },
    
    // Handle presence updates
    handlePresenceUpdate(event: any) {
      const rootStore = getRoot(self) as any;
      const { type, data, userId } = event;
      
      switch (type) {
        case 'presence:user:joined':
          // User joined (silently handled)
          // Update OnlinePresenceStore via action
          rootStore.onlinePresence?.setUserOnline(userId, data);
          break;
          
        case 'presence:user:left':
          // User left (silently handled)
          // Update OnlinePresenceStore via action
          rootStore.onlinePresence?.setUserOffline(userId);
          break;
          
        case 'presence:mindmap:viewers':
          // Updated list of users viewing a mindmap
          rootStore.onlinePresence?.updateMindmapViewers(data.mindmapId, data.viewers);
          break;
          
        case 'presence:cursor:moved':
          // Cursor moved (too verbose, silently handled)
          // Cursor updates handled by cursor:update event
          break;
          
        case 'presence:viewing':
          // User viewing (silently handled)
          // Handled by presence:mindmap:viewers
          break;
          
        default:
          console.warn(`⚠️  [WorkspaceRealtime] Unhandled presence event: ${type}`);
      }
    },
    
    /**
     * Set current mindmap being viewed
     * Notifies server for presence tracking
     */
    setCurrentMindmap(mindmapId: string | null) {
      self.currentMindmapId = mindmapId;
      
      if (self.socket?.connected) {
        self.socket.emit('presence:viewing', { mindmapId });
        console.log(`👁️  [WorkspaceRealtime] Now viewing mindmap: ${mindmapId || 'none'}`);
      }
    },
    
    /**
     * Send cursor position to server
     * Throttled client-side to 20ms (50 fps max)
     */
    sendCursorUpdate(mindmapId: string, x: number, y: number) {
      if (!self.socket?.connected || !self.currentMindmapId) return;
      
      const now = Date.now();
      const timeSinceLastSend = now - self.cursorThrottleTime;
      
      // THROTTLE: Client-side throttling (20ms = 50fps)
      if (timeSinceLastSend < 20) {
        return; // Too soon, skip
      }
      
      self.cursorThrottleTime = now;
      
      self.socket.emit('cursor:move', {
        mindmapId,
        x,
        y,
      });
    },
    
    /**
     * Handle cursor update from another user
     */
    handleCursorUpdate(cursorData: any) {
      const rootStore = getRoot(self) as any;
      
      // Forward to mindmap cursor store if it exists
      if (rootStore.mindmapCursor) {
        rootStore.mindmapCursor.updateCursor(cursorData);
      }
    },
  }));

export type IWorkspaceRealtimeStore = Instance<typeof WorkspaceRealtimeStore>;

